import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useToken } from '@/src/store/token-store'
import { useUser } from '@/src/store/user-store'
import { User } from '@/src/types/user'
import { makeApiRequest } from '@/src/lib/api-request'
import { encryptPassword, getBrowserName, getDeviceType } from '@/src/lib/utils'

export type SignupUserMutationParams = {
  email: string
  password: string
  name: string
  countryCode: string
  phone: string
  gender?: string
  publicChannelName?: string
  orgName?: string
  industryId?: string
  subCategoryID?: string
  type: 'normal' | 'business'
  countryId: string
  dob?: string
  cfToken?: string
}

export const useSignUpMutation = () => {
  const queryClient = useQueryClient()
  const { setToken } = useToken()
  const { setUser, setRefreshToken } = useUser()
  return useMutation({
    mutationKey: ['user', 'signup'],
    mutationFn: async (params: SignupUserMutationParams) => {
      const reqObj =
        params.type === 'normal'
          ? {
              email: params.email,
              password: encryptPassword(params.password),
              gender: params.gender,
              name: params.name,
              countryCode: params.countryCode,
              phone: params.phone,
              signupType: 'email',
              countryId: params.countryId,
              dob: params.dob,
            }
          : {
              email: params.email,
              password: encryptPassword(params.password),
              orgName: params.orgName,
              industryId: params.industryId,
              subCategoryID: params.subCategoryID,
              countryCode: params.countryCode,
              publicChannelName: params.publicChannelName,
              phone: params.phone,
              name: params.name,
              signupType: 'email',
              countryId: params.countryId,
            }

      const challengeToken =
        params.cfToken || localStorage.getItem('cft-UserSignUp') || ''

      const response = await makeApiRequest({
        fallbackError: 'Please enter a valid email address',
        isAuthenticated: false,
        method: 'POST',
        url:
          params.type === 'normal'
            ? '/Login_New/normalUserSignUp'
            : '/Login_New/businessUserSignUp',
        data: reqObj,
        headers: {
          'X-USR-DEVICE': getDeviceType(),
          'X-USR-BROWSER': getBrowserName(),
          'x-api-chlng': challengeToken,
        },
        isV3Api: true,
      })

      if (
        response.respCode === '-1' &&
        response.message.includes('This-channel-name-already-taken')
      ) {
        throw new Error('This channel name is already taken')
      }
      setToken(response.token)
      setRefreshToken(response.refresh_token)
      setUser(response.response)

      return response.response as User
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', 'me'] })
    },
  })
}
